import React from 'react';
import { useSelector } from 'react-redux';
import { Loading } from '../loading';
import { 
  UserState,
  selectDidomiConsentAcceptedOrSkipped, 
  selectItemDetails, 
  selectItemStatus,
  selectIsLoggedIn,
} from '../../ducks';
import { NotFound } from '../NotFound';
import { ItemMetaTags } from './ItemMetaTags';
import c from 'classnames';
import styles from '../item/Item.css';
import { ItemSummaryFooter, MediaItemDetails } from '../item';
import { ItemTags } from './ItemTags';
import { SocialBar } from '../socialbar';
import { KudoCounter } from '../kudocounter';
import { ScrollToTopOnMount } from '../scrolltotop';
import { ReportButton } from '../reportbutton';
import { useHistory } from 'react-router-dom';
import { useAuthCheck } from '../auth';
import { settings } from '../../settings';

/**
 * @param actualItemId - quick fix to handle a special case when
 * old item details is still in store and new actual item is not loaded
 */

const NSFWLoginAuth = ({ history }) => {
  const [userState] = useAuthCheck(history, true);
  return (
    <>
      {(UserState.PENDING === userState || UserState.CHECKED !== userState) && <Loading whatIsLoading='item' />}
    </>
  );
};

export const ItemDetail = ({ staticContext, isModal, isTwitchLiveEmbed }) => {

  const history = useHistory();
  const status = useSelector(selectItemStatus);
  const item = useSelector(selectItemDetails);
  const didomiConsentAcceptedOrSkipped = useSelector(selectDidomiConsentAcceptedOrSkipped);
  const isLoggedIn = useSelector(selectIsLoggedIn);

  if (status === 'loading') {
    return <div><Loading whatIsLoading='item'/></div>;
  }
  if (status === 'error') {
    return <NotFound staticContext={staticContext}/>;
  }
  if (!item || !item.id) {
    return null;
  }
  if (item.nsfw && !isLoggedIn && settings.NSFW_LOGIN_VIEW) {
    return <NSFWLoginAuth history={history}/>;
  }
  return (
    <>
      {!isModal && <ScrollToTopOnMount/>}
      <div className={c(styles.item, styles.card, styles.detail, 'detail-item')}>
        <ItemMetaTags
          item={item}
          url={`//${staticContext ? staticContext.request.get('host') : window.location.host}/item/${item.id}`}
        />
        {item.media.map(({ variants, mediatype, duration }, index) =>
          <React.Fragment key={`ItemDetail-${index}`}>
            <MediaItemDetails
              item={item}
              mediatype={mediatype}
              variants={variants}
              index={index}
              duration={duration}
              counter={item.media.length > 1}
              isLoggedIn={isLoggedIn}/>
            {index === 0 && <>
              <div className={c(styles.action_bar, 'action_bar')}>
                <SocialBar isSharing itemId={item.id} itemTitle={item.title}/>
                <KudoCounter numOfKudos={item.stats.kudos_total} itemId={item.id}/>
              </div>
              <div className={c(styles.summary, styles.detail, 'detail_summary')}>
                <ReportButton itemId={item.id}/>
                <div className={styles.desc} data-id={`id${item.id}`}>
                  <h3 className="title">{item.title}</h3>
                  <>
                    <div className={c(styles.detail_description, 'description')}
                      data-desc="detail"
                      dangerouslySetInnerHTML={
                        { __html: item.description.replace(/<a /g, '<a rel="noreferrer noopener"') }
                      }/>
                    {didomiConsentAcceptedOrSkipped && item.media.filter(({ description }) => !!description).map(
                      itemMedia =>
                        <div className={c(styles.detail_description, styles.detail_media_description, 'description')}
                          data-desc="detail"
                          key={itemMedia}
                          dangerouslySetInnerHTML={{ __html: itemMedia.description }}/>,
                    )}
                    {item.scripts && (
                      <div 
                        style={{ display: 'none' }} 
                        dangerouslySetInnerHTML={{ __html: item.scripts }}
                      />
                    )}
                  </>
                </div>
                <ItemSummaryFooter item={item} isItemDetail/>
                {!isTwitchLiveEmbed && <ItemTags itemTags={item.tags}/>}
              </div>
            </>}
          </React.Fragment>,
        )}
      </div>
    </>
  );
};
